// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TemplateContextRecord = {
  template_context_id: string;
  encounter_phase: string;
  variable_name: string;
  template_name: string;
  data: string;
  matching_criteria: Record<string, unknown>;
  section_name: string;
  created_at?: string;
  last_modified?: string;
};

export type CriteriaRow = {
  field: string;
  operator: string;
  value: string;
  values: string[];
};

export type TemplateContextFormValues = {
  encounter_phase: string;
  variable_name: string;
  template_name: string;
  data: string;
  section_name: string;
  criteria: CriteriaRow[];
};

export type TemplateContextPayload = {
  encounter_phase?: string;
  variable_name?: string;
  template_name?: string;
  data?: string;
  matching_criteria?: Record<string, unknown>;
  section_name?: string;
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const ENCOUNTER_PHASE_OPTIONS = [
  { label: 'In-Visit', value: 'in-visit' },
  { label: 'Pre-Chart', value: 'pre-chart' },
  { label: 'After-Visit', value: 'after-visit' },
];

export const OPERATOR_OPTIONS = [
  { label: 'equals (simple)', value: '$eq_simple' },
  { label: '$eq — Equal to', value: '$eq' },
  { label: '$ne — Not equal to', value: '$ne' },
  { label: '$gt — Greater than', value: '$gt' },
  { label: '$gte — Greater than or equal', value: '$gte' },
  { label: '$lt — Less than', value: '$lt' },
  { label: '$lte — Less than or equal', value: '$lte' },
  { label: '$in — In list', value: '$in' },
  { label: '$nin — Not in list', value: '$nin' },
  { label: '$contains — Contains (case-sensitive)', value: '$contains' },
  { label: '$icontains — Contains (case-insensitive)', value: '$icontains' },
];

export const CRITERIA_FIELD_OPTIONS = [
  { label: 'patient_name', value: 'patient_name' },
  { label: 'patient_phone', value: 'patient_phone' },
  { label: 'patient_email', value: 'patient_email' },
  { label: 'patient_dob', value: 'patient_dob' },
  { label: 'patient_age', value: 'patient_age' },
  { label: 'patient_gender', value: 'patient_gender' },
  { label: 'practitioner_name', value: 'practitioner_name' },
  { label: 'practitioner_phone', value: 'practitioner_phone' },
  { label: 'visit_date', value: 'visit_date' },
  { label: 'clinic_name', value: 'clinic_name' },
  { label: 'encounter_name', value: 'encounter_name' },
  { label: 'description', value: 'description' },
];

export const LIST_OPERATORS = new Set(['$in', '$nin']);

// ---------------------------------------------------------------------------
// Helpers — convert between criteria rows and JSON
// ---------------------------------------------------------------------------

/**
 * Parse a matching_criteria JSON object into flat UI rows.
 */
export const criteriaToRows = (
  criteria: Record<string, unknown> | null | undefined
): CriteriaRow[] => {
  if (!criteria || Object.keys(criteria).length === 0) return [];

  const rows: CriteriaRow[] = [];

  for (const [field, condition] of Object.entries(criteria)) {
    if (
      condition === null ||
      condition === undefined ||
      typeof condition !== 'object' ||
      Array.isArray(condition) ||
      !Object.keys(condition as object).some((k) => k.startsWith('$'))
    ) {
      rows.push({
        field,
        operator: '$eq_simple',
        value: String(condition ?? ''),
        values: [],
      });
    } else {
      for (const [op, val] of Object.entries(
        condition as Record<string, unknown>
      )) {
        if (LIST_OPERATORS.has(op) && Array.isArray(val)) {
          rows.push({
            field,
            operator: op,
            value: '',
            values: val.map(String),
          });
        } else {
          rows.push({
            field,
            operator: op,
            value: String(val ?? ''),
            values: [],
          });
        }
      }
    }
  }

  return rows;
};

/**
 * Convert flat UI rows back into the matching_criteria JSON shape.
 */
export const rowsToCriteria = (
  rows: CriteriaRow[]
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const row of rows) {
    const field = row.field?.trim();
    if (!field) continue;

    const existing = result[field];
    const isExistingObject =
      existing != null &&
      typeof existing === 'object' &&
      !Array.isArray(existing);

    if (row.operator === '$eq_simple') {
      if (isExistingObject) {
        // Another operator already exists for this field — merge as $eq
        // so both constraints are preserved.
        (existing as Record<string, unknown>)['$eq'] = row.value;
      } else {
        result[field] = row.value;
      }
    } else {
      const opValue = LIST_OPERATORS.has(row.operator)
        ? row.values ?? []
        : row.value;

      if (isExistingObject) {
        (existing as Record<string, unknown>)[row.operator] = opValue;
      } else {
        // If a plain value was set by a prior $eq_simple row, promote it
        // to { $eq: value } so we can merge the new operator alongside it.
        const base: Record<string, unknown> =
          existing != null ? { $eq: existing } : {};
        base[row.operator] = opValue;
        result[field] = base;
      }
    }
  }

  return result;
};
